import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://gymea-edu.com.au";
const SITE_NAME = "Gymea Edu";
const DEFAULT_IMAGE = `${SITE_URL}/images/thumbnail_gymea_pink.jpg`;
const LOGO_IMAGE = `${SITE_URL}/Gymea%20Logo%20-%20Round.png`;
const SOFTWARE_DESCRIPTION =
  "Australian AI lesson generator for teachers and Chalkie AI alternative for lesson planning, unit planning, worksheets, report comments and slideshow generation.";
const SOFTWARE_KEYWORDS = [
  "AI lesson generator for teachers",
  "AI lesson plan generator",
  "AI lesson planner",
  "AI for teachers",
  "AI tools for teachers",
  "Australian AI for teachers",
  "Chalkie AI alternative",
  "Australian Chalkie AI alternative",
  "automated lesson planning",
  "AI lesson planning",
  "curriculum aligned lesson generator",
  "Australian curriculum AI",
  "slideshow generation",
  "PowerPoint lesson slides generator",
  "AI worksheet generator",
  "teaching program generator",
];

type SeoConfig = {
  title: string;
  description: string;
  canonicalPath: string;
  noindex?: boolean;
};

const defaultSeo: SeoConfig = {
  title: "AI Lesson Generator for Teachers | Gymea Edu Australia",
  description:
    "Gymea Edu is an Australian AI lesson generator for teachers and Chalkie AI alternative for lesson plans, unit plans, worksheets, report comments and classroom slides.",
  canonicalPath: "/",
};

const routeSeo: Record<string, SeoConfig> = {
  "/": defaultSeo,
  "/home": defaultSeo,
  "/about": {
    title: "Australian AI Lesson Planner for Teachers | About Gymea Edu",
    description:
      "Learn about Gymea Edu, an Australian AI lesson planner and Chalkie AI alternative helping teachers create curriculum-aligned lesson plans, reports and classroom resources.",
    canonicalPath: "/about",
  },
  "/pricing": {
    title: "AI Lesson Generator Pricing | Australian Chalkie AI Alternative",
    description:
      "View Gymea Edu pricing for Australian teachers looking for an AI lesson generator, teaching program generator, report comment tool and Chalkie AI alternative.",
    canonicalPath: "/pricing",
  },
  "/freebie-downloadables": {
    title: "Free AI Lesson Plan and Classroom Slide Samples | Gymea Edu",
    description:
      "Download sample Australian teaching resources from Gymea Edu, including AI lesson plans, units of work, assessments, worksheets and classroom slides.",
    canonicalPath: "/freebie-downloadables",
  },
  "/callback": {
    title: "Authentication | Gymea Edu",
    description: defaultSeo.description,
    canonicalPath: "/callback",
    noindex: true,
  },
  "/email-verified": {
    title: "Email Verified | Gymea Edu",
    description: defaultSeo.description,
    canonicalPath: "/email-verified",
    noindex: true,
  },
  "/dashboard": {
    title: "Dashboard | Gymea Edu",
    description: defaultSeo.description,
    canonicalPath: "/dashboard",
    noindex: true,
  },
  "/profile": {
    title: "Profile | Gymea Edu",
    description: defaultSeo.description,
    canonicalPath: "/profile",
    noindex: true,
  },
  "/documents": {
    title: "My Documents | Gymea Edu",
    description: defaultSeo.description,
    canonicalPath: "/documents",
    noindex: true,
  },
  // Offer page reached from a direct link only — it must never be indexed, and
  // without an entry here it would inherit the "Page Not Found" title.
  "/promo/free-week": {
    title: "Claim Your Two Free Weeks | Gymea Edu",
    description: defaultSeo.description,
    canonicalPath: "/promo/free-week",
    noindex: true,
  },
};

const getSeoConfig = (pathname: string): SeoConfig => {
  return (
    routeSeo[pathname] || {
      title: "Page Not Found | Gymea Edu",
      description: defaultSeo.description,
      canonicalPath: pathname,
      noindex: true,
    }
  );
};

const upsertMeta = (
  selector: string,
  attributeName: "name" | "property",
  attributeValue: string,
  content: string
): void => {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attributeName, attributeValue);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string): void => {
  let tag = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }

  tag.setAttribute("href", href);
};

const upsertJsonLd = (): void => {
  const id = "gymea-edu-structured-data";
  let tag = document.head.querySelector<HTMLScriptElement>(`script#${id}`);

  if (!tag) {
    tag = document.createElement("script");
    tag.id = id;
    tag.type = "application/ld+json";
    document.head.appendChild(tag);
  }

  tag.text = JSON.stringify({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: SITE_NAME,
        url: SITE_URL,
        logo: LOGO_IMAGE,
        areaServed: {
          "@type": "Country",
          name: "Australia",
        },
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: SITE_URL,
        name: SITE_NAME,
        publisher: {
          "@id": `${SITE_URL}/#organization`,
        },
        inLanguage: "en-AU",
      },
      {
        "@type": "SoftwareApplication",
        "@id": `${SITE_URL}/#software`,
        name: SITE_NAME,
        alternateName: [
          "Australian Chalkie AI alternative",
          "Chalkie AI alternative for teachers",
        ],
        applicationCategory: "EducationalApplication",
        operatingSystem: "Web",
        url: SITE_URL,
        description: SOFTWARE_DESCRIPTION,
        keywords: SOFTWARE_KEYWORDS,
        audience: {
          "@type": "EducationalAudience",
          educationalRole: "teacher",
        },
        areaServed: {
          "@type": "Country",
          name: "Australia",
        },
        offers: {
          "@type": "Offer",
          url: `${SITE_URL}/pricing`,
        },
      },
    ],
  });
};

export const Seo: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getSeoConfig(pathname);
    const canonicalUrl = `${SITE_URL}${seo.canonicalPath}`;

    document.title = seo.title;

    upsertMeta(
      'meta[name="description"]',
      "name",
      "description",
      seo.description
    );
    upsertMeta(
      'meta[name="robots"]',
      "name",
      "robots",
      seo.noindex ? "noindex,nofollow" : "index,follow"
    );
    upsertMeta(
      'meta[property="og:site_name"]',
      "property",
      "og:site_name",
      SITE_NAME
    );
    upsertMeta('meta[property="og:type"]', "property", "og:type", "website");
    upsertMeta('meta[property="og:title"]', "property", "og:title", seo.title);
    upsertMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
      seo.description
    );
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    upsertMeta(
      'meta[property="og:image"]',
      "property",
      "og:image",
      DEFAULT_IMAGE
    );
    upsertMeta('meta[property="og:locale"]', "property", "og:locale", "en_AU");
    upsertMeta(
      'meta[name="twitter:card"]',
      "name",
      "twitter:card",
      "summary_large_image"
    );
    upsertMeta(
      'meta[name="twitter:title"]',
      "name",
      "twitter:title",
      seo.title
    );
    upsertMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
      seo.description
    );
    upsertMeta(
      'meta[name="twitter:image"]',
      "name",
      "twitter:image",
      DEFAULT_IMAGE
    );
    upsertLink("canonical", canonicalUrl);
    upsertJsonLd();
  }, [pathname]);

  return null;
};
